import React, { useMemo } from 'react';
import { ButtonBase, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ScheduleDayAppearanceEnum,
  ScheduleTimeStatusEnum,
  WeekDayChipProps
} from '@modules/digital-signage/model/playlist-schedule.model.ts';
import { DAY_MINUTES } from '@modules/digital-signage/utils/schedule.utils.ts';
import {
  SCHEDULE_DAY_APPEARANCE,
  SCHEDULE_LEGEND_PALETTES,
  SCHEDULE_TIME_STATUS_CONFIG,
  getDayAppearance
} from '@modules/digital-signage/utils/schedule-appearance.utils.ts';
import {
  formatDuration,
  formatRange,
  getDayLegendSegments,
  minutesToPercent
} from '@modules/digital-signage/utils/schedule-time.utils.ts';
import { getTimelineHint } from '@modules/digital-signage/utils/schedule-status.utils.ts';

const WeekDayChip: React.FC<WeekDayChipProps> = ({
  day,
  selected,
  hasConflict,
  isOutOfRange,
  status,
  disabled,
  availability,
  ownRanges,
  onToggle
}) => {
  const { t } = useTranslation();
  const { freeMinutes, isFull, segments } = availability;
  const timeStatus = status?.status;
  const isEnded = timeStatus === ScheduleTimeStatusEnum.Ended;
  const appearance = getDayAppearance({
    selected,
    status: timeStatus,
    hasConflict,
    isOutOfRange,
    isFull,
    disabled
  });
  const config = SCHEDULE_DAY_APPEARANCE[appearance];
  const isDisabled =
    !!disabled || isEnded || (!selected && (isFull || isOutOfRange));
  const hint = status
    ? getTimelineHint(status, t)
    : !selected && isOutOfRange
      ? t('digital_signage_schedule_day_out_of_range')
      : '';

  const subLabel = isEnded
    ? t(SCHEDULE_TIME_STATUS_CONFIG[ScheduleTimeStatusEnum.Ended].label)
    : isOutOfRange
      ? t('digital_signage_schedule_day_out_of_range')
      : hasConflict && selected
        ? t('digital_signage_schedule_legend_conflict')
        : selected && timeStatus && SCHEDULE_TIME_STATUS_CONFIG[timeStatus].tone
          ? t(SCHEDULE_TIME_STATUS_CONFIG[timeStatus].label)
          : isFull
            ? t('digital_signage_schedule_day_full')
            : t('digital_signage_schedule_day_free', {
                value: formatDuration(freeMinutes, t)
              });

  const legendSegments = useMemo(
    () => getDayLegendSegments(segments, selected ? ownRanges : []),
    [segments, ownRanges, selected]
  );
  const legendColors = SCHEDULE_LEGEND_PALETTES[config.legend];

  return (
    <Tooltip title={hint} placement="top" disableHoverListener={!hint}>
      <span className="min-w-0">
        <ButtonBase
          disabled={isDisabled}
          onClick={() => onToggle(day)}
          className={`relative! w-full h-15 rounded-[10px]! border! flex-col! justify-start! gap-0.5 pt-2! px-1! transition-all focus-visible:ring-2 focus-visible:ring-(--primary-300) ${config.chip} ${
            isDisabled ? 'cursor-not-allowed!' : ''
          }`}
        >
          {appearance === ScheduleDayAppearanceEnum.Live && (
            <span className="absolute top-1 right-1 flex items-center justify-center w-3 h-3">
              <span className="absolute inset-0 rounded-full bg-(--primary)/35 motion-safe:animate-pulse" />
              <span className="relative w-1.5 h-1.5 rounded-full bg-(--primary)" />
            </span>
          )}
          <Typography
            variant="caption"
            component="span"
            className={`text-[13px]! leading-4.5! font-semibold! ${config.chipTitle}`}
          >
            {t(`digital_signage_schedule_day_${day}`)}
          </Typography>
          <Typography
            variant="caption"
            component="span"
            className={`text-[11px]! leading-3.5! font-medium! whitespace-nowrap ${config.chipSubtitle}`}
          >
            {subLabel}
          </Typography>

          {/* Each part of the bar has its own tooltip; the 12px row is the
              hover area, the 4px line inside it is the visible bar. */}
          <span className="absolute left-2 right-2 bottom-0.5 h-3 flex">
            {legendSegments.map((segment) => (
              <Tooltip
                key={`${segment.type}-${segment.start}`}
                placement="top"
                slotProps={{ tooltip: { className: 'mb-6!' } }}
                title={`${t(`digital_signage_schedule_legend_${segment.type}`)}: ${formatRange(segment, t)}`}
              >
                <span
                  className="h-full flex items-end pb-1"
                  style={{
                    width: minutesToPercent(segment.end - segment.start)
                  }}
                >
                  <span
                    className={`w-full h-1 ${legendColors[segment.type]} ${
                      segment.start === 0 ? 'rounded-l-full' : ''
                    } ${segment.end === DAY_MINUTES ? 'rounded-r-full' : ''}`}
                  />
                </span>
              </Tooltip>
            ))}
          </span>
        </ButtonBase>
      </span>
    </Tooltip>
  );
};

export default WeekDayChip;
